/**
 * useDebounceSearch Hook
 *
 * Provides debounced search functionality with configurable delay.
 * Prevents excessive API calls while user is typing.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseDebounceSearchOptions {
  delay?: number;
  minLength?: number;
  onSearch?: (term: string) => void;
}

export interface UseDebounceSearchReturn {
  searchTerm: string;
  debouncedTerm: string;
  isSearching: boolean;
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
}

export function useDebounceSearch(
  options: UseDebounceSearchOptions = {}
): UseDebounceSearchReturn {
  const { delay = 300, minLength = 0, onSearch } = options;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onSearchRef = useRef(onSearch);

  // Keep onSearch ref updated
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If search term is empty, update immediately
    if (searchTerm === '') {
      setDebouncedTerm('');
      setIsSearching(false);
      onSearchRef.current?.('');
      return;
    }

    // If search term is below minimum length, don't search
    if (searchTerm.length < minLength) {
      setIsSearching(false);
      return;
    }

    // Set searching state
    setIsSearching(true);

    // Set up debounce timeout
    timeoutRef.current = setTimeout(() => {
      setDebouncedTerm(searchTerm);
      setIsSearching(false);
      onSearchRef.current?.(searchTerm);
    }, delay);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchTerm, delay, minLength]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setDebouncedTerm('');
    setIsSearching(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return {
    searchTerm,
    debouncedTerm,
    isSearching,
    setSearchTerm,
    clearSearch,
  };
}

export default useDebounceSearch;
